import React, {useState} from 'react';
import {List, ListItem, ListItemText, IconButton, Box, FormControl, FormHelperText, FormLabel} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

interface Item {
    /**
     * Unique identifier for the item.
     * This is used as the key in the list rendering.
     */
    id: string;
    /**
     * Human readable content of the item to be displayed in the list.
     */
    content: string;
}

interface ReorderableList {
    /**
     * Initial list of items to be displayed in the reorderable list, in the order they should first appear.
     * Each item should have a unique `id` and a `content` string.
     */
    initialItems: Item[];
    /**
     * Callback function that is called when the order of items changes.
     * It receives the new order of items as an argument.
     *
     * Q: Should this instead follow the signature of `onChange` in MUI's inputs like Select or TextField?
     */
    onOrderChange?: (newOrder: Item[]) => void;
    label?: string;
    helperText?: string | React.ReactNode;
}

const ReorderableList: React.FC<ReorderableList> = ({initialItems, onOrderChange, label, helperText}) => {
    const [items, setItems] = useState<Item[]>(initialItems);

    const moveItemUp = (index: number) => {
        if (index === 0) return;

        // Move up one spot in a shallow copy
        const newItems = Array.from(items);
        const [movedItem] = newItems.splice(index, 1);
        newItems.splice(index - 1, 0, movedItem);

        setItems(newItems);
        if (onOrderChange) {
            onOrderChange(newItems);
        }
    };

    const moveItemDown = (index: number) => {
        if (index === items.length - 1) return;

        // Move down one spot in a shallow copy
        const newItems = Array.from(items);
        const [movedItem] = newItems.splice(index, 1);
        newItems.splice(index + 1, 0, movedItem);

        setItems(newItems);
        if (onOrderChange) {
            onOrderChange(newItems);
        }
    };

    return (
        <FormControl component="fieldset" fullWidth>
            {label ? <FormLabel>{label}</FormLabel> : null}
            <FormHelperText>{helperText ? helperText : 'Reorder the items using the arrows.'}</FormHelperText>
            <List dense={true}>
                {items.map((item, index) => (
                    <ListItem
                        key={item.id}
                        secondaryAction={
                            <Box sx={{display: 'flex', gap: 0.5}}>
                                <IconButton edge="end" aria-label="move up" onClick={() => moveItemUp(index)} disabled={index === 0}>
                                    <ArrowUpwardIcon />
                                </IconButton>
                                <IconButton
                                    edge="end"
                                    aria-label="move down"
                                    onClick={() => moveItemDown(index)}
                                    disabled={index === items.length - 1}>
                                    <ArrowDownwardIcon />
                                </IconButton>
                            </Box>
                        }>
                        <ListItemText primary={item.content} />
                    </ListItem>
                ))}
            </List>
        </FormControl>
    );
};

export default ReorderableList;
